<template>
    <div>
        <add-blocks-to-draft
            :draft="Draft"
            @addedBlocks="setDraft"
        ></add-blocks-to-draft>

        <v-divider class="my-2"></v-divider>

        <v-row>
            <v-col cols="12">
                <loading-message
                    v-if="Loading"
                    :message="$t('views.main.drafts.draft_editor.loading_blocks')"
                    class="my-5"
                ></loading-message>

                <v-alert
                    v-else-if="EmptyDraft"
                    border="left"
                    colored-border
                    type="info"
                    elevation="1"
                    class="my-10"
                >
                    {{ $t('views.main.drafts.draft_editor.order_is_empty') }}
                </v-alert>

                <template v-else>
                    <v-slide-y-transition
                        class="d-flex flex-column-reverse"
                        group
                        tag="div"
                    >
                        <block-card
                            v-for="(DraftedBlock, index) in Draft.DraftedBlocks"
                            :key="'block-card-'+index"
                            :units="DraftedBlock.Units"
                            :block="DraftedBlock.Block"
                            :block-price="DraftedBlock.BlockPrice"
                            :index="index"
                            @delete="showDeleteDialog(DraftedBlock)"
                        ></block-card>
                    </v-slide-y-transition>

                </template>
            </v-col>
        </v-row>
        <v-divider class="my-2"></v-divider>
        <v-row>
            <v-col cols="12" class="d-flex align-center">

                <span :class="{'font-weight-bold': true, 'text--secondary': EmptyDraft }"><!-- SUMA: {{ TotalPrice | currencyFormat }} --></span>

                <v-spacer></v-spacer>

                <v-btn
                    v-if="CustomerName"
                    :disabled="!DraftExists"
                    :icon="IsMobile"
                    :rounded="!IsMobile"
                    :text="!IsMobile"
                    color="secondary"
                    @click="showCustomerDialog"
                >
                    <v-icon>mdi-account-edit-outline</v-icon>
                    <span v-if="!IsMobile">{{ CustomerName }}</span>
                </v-btn>

                <v-btn
                    v-else
                    :disabled="!DraftExists"
                    :icon="IsMobile"
                    :rounded="!IsMobile"
                    :text="!IsMobile"
                    color="secondary"
                    @click="showCustomerDialog"
                >
                    <v-icon>mdi-account-plus-outline</v-icon>
                    <span v-if="!IsMobile">
                        {{ $t('views.main.drafts.draft_editor.add_client_button') }}
                    </span>
                </v-btn>

                <v-btn
                    :disabled="EmptyDraft"
                    :icon="IsMobile"
                    :rounded="!IsMobile"
                    :text="!IsMobile"
                    color="secondary"
                    @click="getDraftPdfOffer"
                >
                    <v-icon>mdi-file-download-outline</v-icon>
                    <span v-if="!IsMobile">
                        {{ $t('views.main.drafts.draft_editor.get_offer_button') }}
                    </span>
                </v-btn>

                <v-btn
                    :disabled="EmptyDraft"
                    rounded
                    color="success"
                    @click="Dialog = true"
                    class="ml-3"
                >
                    <template v-if="EditMode">
                        {{ $t('views.main.drafts.draft_editor.continue_button') }}
                    </template>
                    <template v-else>
                        {{ $t('views.main.drafts.draft_editor.place_order_button') }}
                    </template>
                </v-btn>

            </v-col>
        </v-row>

        <v-dialog
            v-model="Dialog"
            persistent
            max-width="600px"
        >
            <v-card>
                <v-card-title class="headline grey lighten-2 mb-5">
                    {{ $t('views.main.drafts.draft_editor.order') }}
                    <v-spacer></v-spacer>
                    <v-btn
                        icon
                        @click="Dialog = false"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <v-select
                                    v-model="OrderForm.ShipmentTypeID"
                                    :items="ShipmentTypes"
                                    item-text="TypeName"
                                    item-value="ShipmentTypeID"
                                    :label="$t('views.main.drafts.draft_editor.shipment_type_label')+'*'"
                                    :loading="ShipmentTypeIndexing"
                                    :clearable="true"
                                    required
                                    :error="$store.getters.hasValidationError('ShipmentTypeID')"
                                    :error-messages="$store.getters.validationError('ShipmentTypeID')"
                                ></v-select>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="OrderForm.ClientOrderName"
                                    :label="$t('views.main.drafts.draft_editor.client_order_name_label')"
                                    :hint="$t('views.main.drafts.draft_editor.client_order_name_hint')"
                                    :error="$store.getters.hasValidationError('ClientOrderName')"
                                    :error-messages="$store.getters.validationError('ClientOrderName')"
                                    maxlength="50"
                                    :counter="50"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="OrderForm.WebAppComments"
                                    :label="$t('views.main.drafts.draft_editor.web_app_comments_label')"
                                    :error="$store.getters.hasValidationError('WebAppComments')"
                                    :error-messages="$store.getters.validationError('WebAppComments')"
                                    maxlength="150"
                                    :counter="150"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-container>
                    <small>* {{ $t('views.main.drafts.draft_editor.field_required') }}</small>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="OrderExists"
                        rounded
                        color="success"
                        :loading="OrderUpdating"
                        @click="orderUpdate"
                    >
                        {{ $t('views.main.drafts.draft_editor.confirm_changes_button') }}
                    </v-btn>
                    <v-btn
                        v-else
                        rounded
                        color="success"
                        :loading="OrderStoring"
                        @click="orderStore"
                    >
                        {{ $t('views.main.drafts.draft_editor.finish_order_button') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-if="Draft"
            v-model="CustomerDialog"
            persistent
            max-width="600px"
        >
            <v-card>
                <v-card-title class="headline grey lighten-2 mb-5">
                    {{ $t('views.main.drafts.draft_editor.client') }}
                    <v-spacer></v-spacer>
                    <v-btn
                        icon
                        @click="hideCustomerDialog"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="CustomerForm.CustomerName"
                                    :label="$t('views.main.drafts.draft_editor.customer_name_label')"
                                    :hint="$t('views.main.drafts.draft_editor.customer_name_hint')"
                                    autofocus
                                    clearable
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        rounded
                        color="success"
                        :loading="CustomerLoading"
                        @click="updateDraftCustomer"
                    >
                        {{ $t('views.main.drafts.draft_editor.save_button') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="DeleteDialog"
            persistent
            width="500"
        >
            <v-card v-if="DraftedBlockToDelete">
                <v-card-title class="headline grey lighten-2 mb-5">
                    {{ $t('views.main.drafts.draft_editor.block_removal') }}
                    <v-spacer></v-spacer>
                    <v-btn
                        icon
                        @click="hideDeleteDialog"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    {{ $t('views.main.drafts.draft_editor.sure_to_remove_block') }}
                    <strong>
                        {{ DraftedBlockToDelete.Block.BlockName }} /
                        {{ DraftedBlockToDelete.Block.System.SystemName }}
                    </strong>?
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="error"
                        rounded
                        :loading="DraftedBlockDeleting"
                        @click="deleteDraftedBlock(DraftedBlockToDelete)"
                    >
                        {{ $t('views.main.drafts.draft_editor.delete_button') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import AddBlocksToDraft from "../../../components/AddBlocksToDraft";
    import BlockCard from '../../../components/BlockCard';
    import LoadingMessage from "../../../components/LoadingMessage";
    import {Paths} from "../../../modules/utils";

    export default {
        name: "DraftEditor",

        components: {
            AddBlocksToDraft,
            BlockCard,
            LoadingMessage,
        },

        data() {
            return {
                Draft: null,
                Loading: false,
                Dialog: false,

                ShipmentTypeIndexing: false,
                ShipmentTypes: [],

                DeleteDialog: false,
                DraftedBlockToDelete: null,
                DraftedBlockDeleting: false,

                CustomerDialog: false,
                CustomerForm: {
                    CustomerName: null
                },
                OrderForm: {
                    DraftID: null,
                    ShipmentTypeID: null,
                    ClientOrderName: null,
                    WebAppComments: null
                },
                CustomerLoading: false,

                OrderStoring: false,
                OrderUpdating: false,
            }
        },

        computed: {
            IsMobile() {
                return this.$vuetify.breakpoint.mobile;
            },
            RouteDraftID() {
                return parseInt(this.$route.params.id);
            },
            EditMode() {
                return !! this.RouteDraftID;
            },
            DraftExists() {
                return this.Draft !== null;
            },
            OrderExists() {
                return this.DraftExists && this.Draft.OrderID !== null;
            },
            DraftedBlocks() {
                return this.DraftExists
                    ? this.Draft.DraftedBlocks
                    : [];
            },
            EmptyDraft() {
                return this.DraftedBlocks.length === 0;
            },
            CustomerName() {
                return this.Draft
                    ? this.Draft.CustomerName
                    : null;
            },
            TotalPrice() {
                return this.EmptyDraft
                    ? 0
                    : this.DraftedBlocks.map(DraftedBlock => DraftedBlock.BlockPrice).reduce(function(a, b) {
                        return a + b;
                    });
            },
        },

        methods: {
            showDraft(DraftID) {
                this.Loading = true;

                this.$store.dispatch('showDraft', DraftID).then(response => {
                    if(response.status === 200) {
                        this.setDraft(response.data.data);
                    } else {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.drafts.draft_editor.loading_draft_failed')
                        );
                    }
                })
                    .catch(() => {})
                    .then(() => {
                        this.Loading = false;
                    });
            },

            updateDraftCustomer() {
                if(!this.Draft) return;

                this.CustomerLoading = true;

                this.$store.dispatch('updateDraftCustomer', {
                    DraftID: this.Draft.DraftID,
                    data: {
                        CustomerName: this.CustomerForm.CustomerName
                    }
                }).then(response => {
                    if(response.status === 200) {
                        this.setDraft(response.data.data);
                        this.hideCustomerDialog();
                    } else {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.drafts.draft_editor.draft_customer_update_failed')
                        );
                    }
                })
                    .catch(() => {})
                    .then(() => {
                        this.CustomerLoading = false;
                    });
            },

            deleteDraftedBlock: function (DraftedBlockToDelete) {
                this.DraftedBlockDeleting = true;

                this.$store.dispatch('deleteDraftedBlock', DraftedBlockToDelete.DraftedBlockID).then(response => {
                    if(response.status === 200) {
                        this.hideDeleteDialog();
                        this.$store.commit(
                            "showSnackbarSuccess",
                            this.$t('views.main.drafts.draft_editor.block_deleted')
                        );
                        this.setDraft(response.data.data);
                    } else {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.drafts.draft_editor.block_deleting_failed')
                        );
                    }
                })
                    .catch(() => {})
                    .then(() => {
                        this.DraftedBlockDeleting = false;
                    });
            },

            shipmentTypeIndex() {
                this.ShipmentTypeIndexing = true;

                this.$store.dispatch('shipmentTypeIndex').then(response => {
                    if(response.status === 200) {
                        this.ShipmentTypes = response.data.data;
                    } else {
                        this.$store.commit(
                            "showSnackbarError",
                        this.$t('views.main.drafts.draft_editor.loading_shipment_types_failed')
                        );
                    }
                })
                    .catch(() => {})
                    .then(() => {
                        this.ShipmentTypeIndexing = false;
                    });
            },

            orderStore() {
                this.OrderStoring = true;

                this.$store.dispatch('orderStore', this.OrderForm).then(response => {
                    if([200, 201].includes(response.status)) {
                        this.$router.push({name: 'orders'});
                    } else {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.drafts.draft_editor.order_saving_failed')
                        );
                    }
                })
                    .catch(() => {})
                    .then(() => {
                        this.OrderStoring = false;
                    });
            },

            orderUpdate() {
                this.OrderUpdating = true;

                this.$store.dispatch('orderUpdate', this.OrderForm).then(response => {
                    if(response.status === 200) {
                        this.$router.push({name: 'orders'});
                    } else {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.drafts.draft_editor.order_saving_failed')
                        );
                    }
                })
                    .catch(() => {})
                    .then(() => {
                        this.OrderUpdating = false;
                    });
            },

            setDraft(Draft) {
                this.Draft = Draft;
            },

            showDeleteDialog: function(DraftedBlock) {
                this.DraftedBlockToDelete = DraftedBlock;
            },

            hideDeleteDialog: function() {
                this.DeleteDialog = false;
            },

            showCustomerDialog: function() {
                this.CustomerDialog = true;
            },

            hideCustomerDialog: function() {
                this.CustomerDialog = false;
            },

            getDraftPdfOffer: function () {
                if( !this.DraftExists ) {
                    return;
                }
                location.href = Paths.pdfOffer(this.Draft.DraftID);
            },
        },

        created() {
            this.shipmentTypeIndex();

            if(this.EditMode) {
                this.showDraft(this.RouteDraftID);
            }
        },

        watch: {
            CustomerName (newCustomerName) {
                this.CustomerForm.CustomerName = newCustomerName;
            },
            Draft (newDraft) {
                this.OrderForm.DraftID = newDraft.DraftID;
                this.OrderForm.ShipmentTypeID = newDraft.ShipmentTypeID;
                this.OrderForm.ClientOrderName = newDraft.ClientOrderName;
                this.OrderForm.WebAppComments = newDraft.WebAppComments;
            },
            DraftedBlockToDelete: function (new_value) {
                if(new_value !== null) {
                    this.DeleteDialog = true;
                }
            },
            DeleteDialog: function (new_value) {
                if(!new_value) {
                    this.DraftedBlockToDelete = null;
                }
            },
        },
    }
</script>

<style scoped>

</style>
